// src/features/appraisal/PerformanceAppraisalPage.jsx
import React, {useState, useEffect, useMemo} from 'react';
import { getAuthHeader } from '../../utils/auth';
import { useAuth } from './../auth/AuthContext';
import { useDataEngine } from '@dhis2/app-runtime';
import { CircularLoader, NoticeBox } from '@dhis2/ui';
import { useParams, useSearchParams } from 'react-router-dom';
import AppraisalForm from './AppraisalForm';
import { Users, ChevronLeft, UserCircle } from 'lucide-react';
import './AppraisalForm.css';
import { useCurrentUserPosition } from './hooks/useCurrentUserPosition';
import { useAppraisalEvents } from './hooks/useAppraisalEvents';
import {
  APPRAISAL_PROGRAM_STAGES,
  APPRAISAL_PROGRAM,
  DATA_ELEMENTS,
  APPRAISAL_STATUSES,
} from './api/appraisalConstants';
import { POSITION_ATTRIBUTES } from '../auth/utils/reportingStructure';
import { BsExclamationTriangle } from 'react-icons/bs';

const extractAttributes = (attributes = []) => {
  const map = {};
  attributes.forEach((attr) => (map[attr.attribute] = attr.value));
  return map;
};

const PerformanceAppraisalPage = ({ reportType = null, readOnlyReport = false }) => {
  // ✅ All hooks at the very top
  const { user } = useAuth();
  const { type: routeType } = useParams();
  const [searchParams] = useSearchParams();
  const [subordinates, setSubordinates] = useState([]);
  const subordinateTei = searchParams.get('subordinate'); 
  const engine = useDataEngine();

  const [fiscalYear, setFiscalYear] = useState({ start: '', end: '' });
  const [fiscalYearLoaded, setFiscalYearLoaded] = useState(false);
  const [availableFiscalYears, setAvailableFiscalYears] = useState([]);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState(null);
  const [showHistoricalView, setShowHistoricalView] = useState(false);
  const [historicalAppraisalData, setHistoricalAppraisalData] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isManager, setIsManager] = useState(false);

  // Load position for current user (manager or employee)
  const { currentUserPosition, loading: positionLoading } =
    useCurrentUserPosition(user?.id, user?.organisationUnits || []);

  // ✅ Validate type
  const type = reportType || routeType;
  const isValidType = type === 'mid-year' || type === 'year-end';
  const programStage = isValidType
    ? type === 'mid-year'
      ? APPRAISAL_PROGRAM_STAGES.MID_YEAR
      : APPRAISAL_PROGRAM_STAGES.YEAR_END
    : null;

  // ✅ Determine TARGET TEI: subordinate (if viewing team) OR own position
  const targetTei = subordinateTei || currentUserPosition?.trackedEntityInstance || null;
  const targetOrgUnit = currentUserPosition?.orgUnit || null;

   const selectedSubordinate = useMemo(
      () => subordinates.find(sub => sub.tei === subordinateTei),
      [subordinates, subordinateTei]
   );
  
  
  useEffect(() => {
      if (!user?.id || !currentUserPosition?.trackedEntityInstance) return;
      
      const loadSubordinates = async () => {
        try {
          // Step 1: Query positions where REPORTING_TO = manager's position TEI
          const response = await engine.query({
            subordinates: {
              resource: 'trackedEntityInstances',
              params: {
                trackedEntityType: 'UGGo0Ebjl0E',
                ou: currentUserPosition?.orgUnit,
                ouMode: 'ACCESSIBLE',
                filter: `${POSITION_ATTRIBUTES.REPORTING_TO}:eq:${currentUserPosition.trackedEntityInstance}`,
                fields: 'trackedEntityInstance,attributes[attribute,value]'
              }
            }
          });
          
          // Step 2: Map positions with incumbent info
          const subs = response.subordinates?.trackedEntityInstances?.map(tei => {
            const attrs = extractAttributes(tei.attributes || []);
            return {
              tei: tei.trackedEntityInstance,
              positionTitle: attrs[POSITION_ATTRIBUTES.POSITION_TITLE] || 'Unknown Position',
              incumbentId: attrs[POSITION_ATTRIBUTES.CURRENT_INCUMBENT]
            };
          }) || [];

          // Step 3: Fetch user names from /api/users endpoint
          const subsWithNames = await Promise.all(subs.map(async sub => {
            if (sub.incumbentId) {
              try {
    
                const userRes = await fetch(`${window.location.origin}/api/users/${sub.incumbentId}`, {
                  headers: {
                    ...getAuthHeader(),  
                    'Content-Type': 'application/json'
                  }
                });
                
                if (userRes.ok) {
                  const userData = await userRes.json();
                  return {
                    ...sub,
                    displayName: userData.displayName || userData.name || sub.incumbentId
                  };
                }
              } catch (err) {
                console.warn('Failed to fetch user name for', sub.incumbentId, err);
              }
            }
            // Fallback: Use position title if name fetch fails
            return { ...sub, displayName: sub.positionTitle };
          }));
          
          console.log('👥 Subordinates loaded:', subsWithNames);
          setSubordinates(subsWithNames);
        } catch (err) {
          console.error('Failed to load subordinates:', err);
          setSubordinates([]);
        }
      };

      loadSubordinates();
    }, [user?.id, currentUserPosition, engine]);


  useEffect(() => {
      const loadFiscalYearsFromAppraisals = async () => {
        if (!targetTei || !programStage) return;
        
        try {
          // Query all appraisal events to get distinct fiscal years
          const response = await engine.query({
            events: {
              resource: 'events',
              params: {
                program: APPRAISAL_PROGRAM,
                programStage: programStage,
                trackedEntityInstance: targetTei,
                fields: 'eventDate,event',
                pageSize: 200,
                ouMode: 'ACCESSIBLE'
              }
            }
          });
          
          const events = response.events?.events || [];
          
          // Extract unique years from event dates
          const uniqueYears = new Set();
          events.forEach(event => {
            if (event.eventDate) {
              const eventYear = new Date(event.eventDate).getFullYear();
              uniqueYears.add(eventYear);
            }
          });
          
          // Also include current fiscal year based on date
          const now = new Date();
          const currentYear = now.getFullYear();
          const julyFirstCurrentYear = new Date(currentYear, 6, 1);
          let currentFiscalStartYear = now >= julyFirstCurrentYear ? currentYear : currentYear - 1;
          uniqueYears.add(currentFiscalStartYear);
          
          // Sort years in descending order
          const sortedYears = Array.from(uniqueYears).sort((a, b) => b - a);
          
          // Build fiscal year list
          const fiscalYearsList = sortedYears.map(year => ({
            year: year,
            start: `${year}-07-01`,
            end: `${year + 1}-06-30`,
            label: `${year} - ${year + 1}`,
            isCurrent: year === currentFiscalStartYear
          }));
          
          setAvailableFiscalYears(fiscalYearsList);
          console.log('✅ Generated appraisal fiscal years:', fiscalYearsList);
          
        } catch (error) {
          console.error('Failed to load appraisal fiscal years:', error);
          setAvailableFiscalYears([]);
        }
      };
      
      if (targetTei && programStage) {
        loadFiscalYearsFromAppraisals();
      }
   }, [targetTei, programStage, engine]);

   // Load current fiscal year configuration from metadata
      useEffect(() => {
        const orgUnitId = user?.organisationUnits?.[0]?.id;
        
        if (!orgUnitId || !engine || fiscalYearLoaded) return;

        const loadFiscalYear = async () => {
          try {
            console.log('🔍 Loading fiscal year for orgUnit:', orgUnitId);
            
            const response = await engine.query({
              events: {
                resource: 'events',
                params: {
                  program: 'NkReudUAW4F',
                  programStage: 'htIWgNQWEMm',
                  ou: orgUnitId,
                  ouMode: 'ACCESSIBLE',
                  pageSize: 1,
                  order: 'eventDate:desc',
                  fields: 'event,dataValues[dataElement,value]'
                }
              }
            });
            
            const event = response.events?.events?.[0];
            let startPattern = '', endPattern = '';
            
            if (event) {
              event.dataValues?.forEach(dv => {
                if (dv.dataElement === 'U55MqwBcRAe') startPattern = dv.value;
                if (dv.dataElement === 'kgdk4NXu4f5') endPattern = dv.value;
              });
            }
            
            if (startPattern && endPattern) {
              const startDate = new Date(startPattern);
              const endDate = new Date(endPattern);
              const now = new Date();
              const currentYear = now.getFullYear();
              
              // For July 1 start, fiscal year runs from July 1 to June 30
              let fiscalStartDate, fiscalEndDate;
              const julyFirstCurrentYear = new Date(currentYear, 6, 1);
              
              if (now >= julyFirstCurrentYear) {
                fiscalStartDate = new Date(currentYear, startDate.getMonth(), startDate.getDate());
                fiscalEndDate = new Date(currentYear + 1, endDate.getMonth(), endDate.getDate());
              } else {
                fiscalStartDate = new Date(currentYear - 1, startDate.getMonth(), startDate.getDate());
                fiscalEndDate = new Date(currentYear, endDate.getMonth(), endDate.getDate());
              }
              
              const formattedStart = `${fiscalStartDate.getFullYear()}-${String(fiscalStartDate.getMonth() + 1).padStart(2, '0')}-${String(fiscalStartDate.getDate()).padStart(2, '0')}`;
              const formattedEnd = `${fiscalEndDate.getFullYear()}-${String(fiscalEndDate.getMonth() + 1).padStart(2, '0')}-${String(fiscalEndDate.getDate()).padStart(2, '0')}`;
              
              setFiscalYear({ start: formattedStart, end: formattedEnd });
            } else {
              const now = new Date();
              const currentYear = now.getFullYear();
              const defaultStart = now >= new Date(currentYear, 6, 1) 
                ? `${currentYear}-07-01` 
                : `${currentYear - 1}-07-01`;
              const defaultEnd = now >= new Date(currentYear, 6, 1)
                ? `${currentYear + 1}-06-30`
                : `${currentYear}-06-30`;
              setFiscalYear({ start: defaultStart, end: defaultEnd });
            }
            setFiscalYearLoaded(true);
          } catch (error) {
            console.error('❌ Failed to load fiscal year:', error);
            const now = new Date();
            const currentYear = now.getFullYear();
            setFiscalYear({ 
              start: `${currentYear - 1}-07-01`, 
              end: `${currentYear}-06-30` 
            });
            setFiscalYearLoaded(true);
          }
        };
        
        loadFiscalYear();
    }, [user?.organisationUnits?.[0]?.id, engine, fiscalYearLoaded]);

    const loadHistoricalAppraisal = async (year) => {
        setLoading(true);
        try {
          const fiscalConfig = availableFiscalYears.find(fy => fy.year === year);
          if (!fiscalConfig) return;
          
          // Query appraisal events for the selected fiscal year
          const response = await engine.query({
            events: {
              resource: 'events',
              params: {
                program: APPRAISAL_PROGRAM,
                programStage: programStage,
                trackedEntityInstance: targetTei,
                fields: 'event,dataValues[dataElement,value],status',
                filter: `eventDate:ge:${fiscalConfig.start}:eventDate:le:${fiscalConfig.end}`,
                pageSize: 1,
                ouMode: 'ACCESSIBLE'
              }
            }
          });
          
          const event = response.events?.events?.[0];
          if (event) {
            const dv = (de) => event.dataValues?.find(d => d.dataElement === de)?.value || '';
            const rawStatus = dv(DATA_ELEMENTS.APPRAISAL_STATUS);
            
            setHistoricalAppraisalData({
              employeeSelfRatingTask: dv(DATA_ELEMENTS.EMPLOYEE_SELF_RATING_TASK),
              employeeSelfRatingBehavioural: dv(DATA_ELEMENTS.EMPLOYEE_SELF_RATING_BEHAVIOURAL),
              employeeSelfComments: dv(DATA_ELEMENTS.EMPLOYEE_COMMENTS),
              managerRatingTask: dv(DATA_ELEMENTS.MANAGER_RATING_TASK),
              managerRatingBehavioural: dv(DATA_ELEMENTS.MANAGER_RATING_BEHAVIOURAL),
              managerComments: dv(DATA_ELEMENTS.MANAGER_COMMENTS),
              appraisalStatus: rawStatus ? rawStatus.toLowerCase() : APPRAISAL_STATUSES.DRAFT,
              eventId: event.event,
            });
            setShowHistoricalView(true);
            setSelectedFiscalYear(year);
          } else {
            // No appraisal found for that year
            setHistoricalAppraisalData(null);
            setShowHistoricalView(true);
            setSelectedFiscalYear(year);
          }
        } catch (error) {
          console.error('Failed to load historical appraisal:', error);
          setError('Could not load historical data');
        } finally {
          setLoading(false);
        }
   };

  
  // ✅ Load appraisal event for TARGET TEI (critical: use targetTei, not positionTEI)
  const { appraisalEvent, loading: eventLoading, refetch } = useAppraisalEvents(
    targetTei,              // ← Use targetTei (subordinate or self)
    programStage,
    targetOrgUnit,          // ← Use targetOrgUnit
    engine
  );

  {/* Fiscal Year Selector - Show for managers and admins */}
  {((isManager && subordinates.length > 0) || !readOnlyReport) && availableFiscalYears.length > 0 && (
    <div style={{ 
      marginBottom: '24px', 
      padding: '16px', 
      backgroundColor: '#f0f4f8', 
      borderRadius: '8px',
      border: '1px solid #d0d7de'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <label style={{ fontWeight: 600, marginRight: '12px' }}>Appraisal Period:</label>
          <span style={{ 
            padding: '4px 12px', 
            backgroundColor: fiscalYear.start && fiscalYear.end ? '#4caf50' : '#ff9800',
            color: 'white',
            borderRadius: '4px',
            fontWeight: 500
          }}>
            {fiscalYear.start && fiscalYear.end 
              ? `${new Date(fiscalYear.start).getFullYear()} - ${new Date(fiscalYear.end).getFullYear()}`
              : 'Not Configured'}
          </span>
        </div>
        
        {availableFiscalYears.length > 0 && (
          <div>
            <label style={{ fontWeight: 600, marginRight: '12px' }}>View Historical:</label>
            <select
              value={selectedFiscalYear || ''}
              onChange={(e) => {
                const yearValue = parseInt(e.target.value);
                if (yearValue) {
                  const yearData = availableFiscalYears.find(y => y.year === yearValue);
                  if (yearData) {
                    if (yearData.isCurrent) {
                      // If current year selected, go back to current view
                      setSelectedFiscalYear(null);
                      setShowHistoricalView(false);
                      setHistoricalAppraisalData(null);
                      // Refresh current data
                      window.location.reload();
                    } else {
                      loadHistoricalAppraisal(yearData.year);
                    }
                  }
                } else {
                  setSelectedFiscalYear(null);
                  setShowHistoricalView(false);
                  setHistoricalAppraisalData(null);
                  window.location.reload();
                }
              }}
              style={{
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                backgroundColor: 'white',
                minWidth: '150px'
              }}
            >
              <option value="">-- Current Year --</option>
              {availableFiscalYears
                .filter(fy => !fy.isCurrent)
                .map(fy => (
                  <option key={fy.year} value={fy.year}>
                    {fy.label}
                  </option>
                ))}
            </select>
          </div>
        )}
      </div>
      
      {showHistoricalView && selectedFiscalYear && (
        <div style={{
          marginTop: '12px',
          padding: '8px 12px',
          backgroundColor: '#fff3e0',
          borderRadius: '4px',
          fontSize: '0.9rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>📅 Viewing historical data for fiscal year {selectedFiscalYear} - {selectedFiscalYear + 1}</span>
          <button
            onClick={() => {
              setSelectedFiscalYear(null);
              setShowHistoricalView(false);
              setHistoricalAppraisalData(null);
              window.location.reload();
            }}
            style={{
              padding: '4px 12px',
              backgroundColor: '#ff9800',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Back to Current
          </button>
        </div>
      )}
    </div>
  )}

  // UI States

  if (!isValidType) {
    return (
      <div className="apr-page-wrap">
        <NoticeBox error title="Invalid Appraisal Type">
          Please select a valid appraisal type:
          <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
            <li><a href="/performance-appraisal/mid-year">Mid-Year Appraisal</a></li>
            <li><a href="/performance-appraisal/year-end">Year-End Appraisal</a></li>
          </ul>
        </NoticeBox>
      </div>
    );
  }

  if (positionLoading) {
    return (
      <div className="apr-loading">
        <CircularLoader small />
        <span>Loading your position…</span>
      </div>
    );
  }

  if (!currentUserPosition) {
    return (
      <div className="apr-page-wrap">
        <div className="apr-no-position">
          <h4><span><BsExclamationTriangle size={14}/></span> No Position Assigned</h4>
          <p>Your account (<strong>{user?.username}</strong>) is not linked to any position in the system.</p>
          <p><strong>Name:</strong> {user?.name}</p>
          <p><strong>Organisation:</strong> {user?.organisationUnits?.map(ou => ou.name).join(', ') || 'None'}</p>
        </div>
      </div>
    );
  }

  if (eventLoading) {
    return (
      <div className="apr-loading">
        <CircularLoader small />
        <span>Loading appraisal data…</span>
      </div>
    );
  }

  const isMidYear = type === 'mid-year';
  

  // Render form with TARGET TEI (subordinate or self)
  return (
    <div className="apr-page-wrap">

      {/* ── Page Top Bar ──────────────────────────────────────── */}
      <div className="apr-page-top">
        <div>
          <h2 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 700, color: 'var(--apr-text)' }}>
            {isMidYear ? 'Mid-Year' : 'Year-End'} Performance Appraisal
          </h2>
          <div style={{ fontSize: '0.85rem', color: 'var(--apr-text-muted)', marginTop: '0.2rem' }}>
            {subordinateTei
              ? `Reviewing: ${selectedSubordinate?.displayName || selectedSubordinate?.positionTitle || 'Team Member'}`
              : 'Reviewing your own appraisal'}
          </div>
        </div>
        <span className={`apr-type-badge ${isMidYear ? 'apr-type-badge--mid' : 'apr-type-badge--year'}`}>
          {isMidYear ? '🗓 Mid-Year' : '📅 Year-End'}
        </span>
      </div>

      {/* ── Subordinate Selector ─────────────────────────────── */}
      {!readOnlyReport && subordinates.length > 0 && (
        <div className="apr-selector-bar" data-print-hidden="true">
          <span className="apr-selector-icon"><Users size={18} /></span>
          <label className="apr-selector-label" htmlFor="subordinate-select">Review Team Member:</label>
         <select 
            value={subordinateTei || ''}
            onChange={(e) => {
              const tei = e.target.value;
              if (tei) {
                window.history.replaceState({}, '', `?subordinate=${tei}`);
              } else {
                window.history.replaceState({}, '', window.location.pathname);
              }
              window.location.reload();
            }}
            style={{ padding: 4, borderRadius: 4 }}
          >
            <option value="">-- Select team member --</option>
            {subordinates.map(sub => (
              <option key={sub.tei} value={sub.tei}>
                {sub.displayName} - {sub.positionTitle}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* ── Context Banner (when viewing a subordinate) ───────── */}
      {!readOnlyReport && subordinateTei && selectedSubordinate && (
          <div className="apr-context-banner">
            <span className="apr-context-banner-icon"><UserCircle size={20} /></span>
            <div>
              <div>
                Viewing appraisal for{' '}
                <span className="apr-context-banner-name">
                  {selectedSubordinate.displayName || selectedSubordinate.positionTitle || 'Team Member'}
                </span>
              </div>
              {selectedSubordinate.positionTitle && selectedSubordinate.displayName && (
                <div className="apr-context-banner-id">
                  Position: {selectedSubordinate.positionTitle}
                </div>
              )}
            </div>
            <button
              className="apr-back-link"
              data-print-hidden="true"
              onClick={() => {
                window.history.replaceState({}, '', window.location.pathname);
                window.location.reload();
              }}
              style={{ 
                  boxShadow: "0 8px 10px rgba(108, 105, 105, 0.5)",
                  color: 'white',
                  backgroundColor: 'rgb(228, 138, 3)',
                  borderRadius: '8px',
                  border: 'none',
                  width: '170px',
                  height: '35px',
                  cursor: 'pointer' }}
            >
              <ChevronLeft size={14} />
              Back to my appraisal
            </button>
          </div>
        )}

      {/* ── Appraisal Form ───────────────────────────────────── */}
      <AppraisalForm
        positionTEI={targetTei}
        programStage={programStage}
        orgUnit={targetOrgUnit}
        readOnlyReport={readOnlyReport || showHistoricalView} // Make read-only for historical view
        appraisalEvent={appraisalEvent}
        appraisalProgram={APPRAISAL_PROGRAM}
        dataElements={DATA_ELEMENTS}
        appraisalStatusOptions={APPRAISAL_STATUSES}
        onSaved={refetch}
        isHistoricalView={showHistoricalView}
      />
    </div>
  );
};

export default PerformanceAppraisalPage;
